import { useRef, useState } from 'react';
import { api, errorText } from '../api';
import { t } from '../i18n';
import { useToast } from '../lib/toast';

// „Teilen“ neben „PDF herunterladen“ (Sam 12.10.: am iPhone öffnet sich die PDF nur, speichern oder per WhatsApp
// schicken ging nicht). Öffnet das Teilen-Menü des Geräts mit der Datei: „In Dateien sichern“, WhatsApp, Mail usw.
// Nur wo der Browser Dateien teilen kann (iPhone, Android-Chrome, Mac, Windows), sonst kein Knopf; die Android-App
// kann es nicht. Geladen wird die Datei erst beim Antippen. Safari öffnet das Menü nur direkt nach dem Tippen; dauert
// das Laden zu lange, liegt die Datei danach bereit und ein zweites Tippen reicht.

export function canShareFiles(): boolean {
  try {
    const probe = new File([new Uint8Array(1)], 'x.pdf', { type: 'application/pdf' });
    return typeof navigator.share === 'function' && navigator.canShare?.({ files: [probe] }) === true;
  } catch {
    return false;
  }
}

export function ShareFile({ url, name, className = 'btn2' }: { url: string; name: string; className?: string }) {
  const toast = useToast();
  const file = useRef<{ url: string; p: Promise<File> } | null>(null);
  const [busy, setBusy] = useState(false);
  if (!canShareFiles()) return null;

  const load = () => {
    if (file.current?.url !== url) {
      const p = api.file(url).then((b) => new File([b], name, { type: b.type || 'application/octet-stream' }));
      // nach einem Fehler beim nächsten Tippen neu laden
      p.catch(() => {
        if (file.current?.p === p) file.current = null;
      });
      file.current = { url, p };
    }
    return file.current.p;
  };
  async function share() {
    setBusy(true);
    try {
      await navigator.share({ files: [await load()] });
    } catch (err) {
      // abgebrochen: nichts zu melden
      if (err instanceof DOMException && err.name === 'AbortError') return;
      if (err instanceof DOMException && err.name === 'NotAllowedError') {
        toast(t('Die Datei ist bereit. Bitte noch einmal auf „Teilen“ tippen.'));
        return;
      }
      toast(errorText(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    // schon beim Berühren laden, damit das Menü direkt nach dem Tippen aufgehen kann
    <button type="button" className={className} onPointerDown={() => void load().catch(() => {})} onClick={share} disabled={busy}>
      {t('Teilen')}
    </button>
  );
}
